// Story 1.38 (Twitch) + Story 7.20 (YouTube) — optional channel
// ownership verification on the application form and pending card.
//
// Proving you own a channel you're linking is NOT required to apply —
// but it's the single strongest signal a reviewer has, so the copy makes
// clear it speeds approval up dramatically. Connecting runs the real
// OAuth PKCE flow (full-page navigation to the backend /oauth/start,
// which 302s to the provider); on return the backend has recorded the
// claim and bounces back with ?<provider>=verified.
//
// The form owns draft-save: `onBeforeConnect` runs before we leave the
// page so nothing typed is lost across the OAuth round-trip.
//
// ONE BOX, ONE ROW PER PROVIDER. Rendering a separate bordered card per
// provider would repeat the "Verify a channel (optional)" header, and
// that header is the part doing the persuading — it should be said once.

import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { Button } from "@greenhold/components";

import {
  identityClaimQueryKey,
  useIdentityClaim,
  type IdentityProvider,
} from "./useIdentityClaim";

interface ProviderMeta {
  label: string;
  startUrl: string;
  /** Render NOTHING when the provider is unavailable to this caller,
   *  instead of the "not enabled on this environment" note.
   *
   *  True only for YouTube, and the difference is deliberate. Twitch is
   *  a shipped feature, so its absence is a misconfiguration worth
   *  telling someone about. YouTube is deliberately hidden during the
   *  beta while Google's app verification is pending — announcing "not
   *  enabled yet" would advertise exactly what we are hiding. */
  hideWhenUnavailable: boolean;
}

const PROVIDERS: Record<IdentityProvider, ProviderMeta> = {
  twitch: {
    label: "Twitch",
    startUrl: "/api/v1/creators/me/identity/twitch/oauth/start",
    hideWhenUnavailable: false,
  },
  youtube: {
    label: "YouTube",
    startUrl: "/api/v1/creators/me/identity/youtube/oauth/start",
    hideWhenUnavailable: true,
  },
  kick: {
    label: "Kick",
    startUrl: "/api/v1/creators/me/identity/kick/oauth/start",
    // Kick has no app-review gate and no unverified-app warning, so
    // there is nothing to contain — an unconfigured deployment is a
    // misconfiguration worth saying out loud, exactly like Twitch.
    hideWhenUnavailable: false,
  },
};

const DEFAULT_PROVIDERS: IdentityProvider[] = ["twitch", "youtube", "kick"];

interface Props {
  /** Persist the in-progress form before the OAuth redirect so the
   *  applicant's work survives leaving the page. Omitted on surfaces
   *  with nothing to save (e.g. the pending-application card). */
  onBeforeConnect?: () => Promise<void>;
  /** Defaults to every provider. Narrow it to render a subset. */
  providers?: IdentityProvider[];
}

export function IdentityVerifyField({
  onBeforeConnect,
  providers = DEFAULT_PROVIDERS,
}: Props) {
  return (
    <div className="space-y-3 rounded-md border border-brand-forest-200 bg-brand-paper-50 p-4">
      <div>
        <p className="font-semibold m-0">Verify a channel (optional)</p>
        <p className="text-sm opacity-80 m-0">
          Not required to apply — but proving you own at least one linked
          channel speeds up approval <em>by a landslide</em>. It&apos;s the
          strongest signal our reviewers have.
        </p>
      </div>

      {providers.map((provider) => (
        <ProviderRow
          key={provider}
          provider={provider}
          onBeforeConnect={onBeforeConnect}
        />
      ))}
    </div>
  );
}

// A child component rather than a loop body: each row owns a
// `useIdentityClaim` call, and hooks cannot be called from inside a map.
function ProviderRow({
  provider,
  onBeforeConnect,
}: {
  provider: IdentityProvider;
  onBeforeConnect?: () => Promise<void>;
}) {
  const meta = PROVIDERS[provider];
  const claimQuery = useIdentityClaim(provider);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connecting, setConnecting] = useState(false);

  // One-shot callback-outcome toast (?<provider>=verified|denied), then
  // refetch the claim so the badge reflects the just-proved account.
  useEffect(() => {
    const outcome = searchParams.get(provider);
    if (!outcome) return;
    if (outcome === "verified") {
      toast.success(`${meta.label} ownership verified.`);
      queryClient.invalidateQueries({
        queryKey: identityClaimQueryKey(provider),
      });
    } else if (outcome === "denied") {
      toast.error(`${meta.label} verification was cancelled.`);
    }
    const next = new URLSearchParams(searchParams);
    next.delete(provider);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, queryClient, provider, meta.label]);

  const claim = claimQuery.data?.data ?? null;
  const enabled = claimQuery.data?.link_enabled ?? false;
  // Provider not available to this caller: not configured on the
  // deployment (Twitch), or outside the beta allowlist (YouTube).
  const unavailable = !!claimQuery.data && !enabled && !claim;

  // The hidden case — the SPA's half of it. The server-side half is the
  // one that matters: /oauth/start 404s for the same caller, so this is
  // tidiness, not the control.
  if (unavailable && meta.hideWhenUnavailable) return null;

  const handleConnect = async () => {
    setConnecting(true);
    try {
      if (onBeforeConnect) await onBeforeConnect();
      // Full-page navigation — the backend 302s to the provider.
      window.location.href = meta.startUrl;
    } catch {
      setConnecting(false);
      toast.error("Couldn't save your progress — try again before verifying.");
    }
  };

  return (
    <div className="space-y-2" data-testid={`identity-row-${provider}`}>
      {claimQuery.isPending ? (
        <p className="text-sm opacity-70 m-0">Loading…</p>
      ) : claim ? (
        <p
          className="text-sm m-0 flex items-center gap-2"
          data-testid={`${provider}-verified-status`}
        >
          <span aria-hidden="true">✓</span>
          <span>
            <strong>{meta.label} verified</strong> as @{claim.login}. You can
            still re-verify a different account below.
          </span>
        </p>
      ) : unavailable ? (
        <p
          className="text-sm opacity-80 m-0"
          data-testid={`${provider}-unavailable-status`}
        >
          {meta.label} verification isn&apos;t enabled on this environment yet.
        </p>
      ) : (
        <p
          className="text-sm opacity-80 m-0"
          data-testid={`${provider}-unverified-status`}
        >
          Your {meta.label} channel isn&apos;t verified yet.
        </p>
      )}

      <Button
        type="button"
        variant={claim ? "tertiary" : "secondary"}
        onClick={handleConnect}
        disabled={connecting || unavailable}
        data-testid={`${provider}-verify-button`}
      >
        {connecting
          ? `Redirecting to ${meta.label}…`
          : claim
            ? `Re-verify ${meta.label}`
            : `Verify with ${meta.label}`}
      </Button>
    </div>
  );
}
